// Who may read the session through /_session-bridge (HOTFIX 2026-10-08, P0).
//
// Both bridge pages (workspace shell + canvas) used to answer any origin
// matching /^https?:\/\/[^.]+\.symbols\.app$/ — http included, and any
// one-label host such as preview.symbols.app, which renders user project
// code. Rule now: only the exact shell host and comm host (commHost.js) of the
// SAME channel as the page, https, no port; a *.localhost page answers only
// *.localhost peers. Channel hosts come from @symbo.ls/channels.
//
// No channel has a canvas host any more: the standalone canvas hosts
// (`edit.symbols.app`, `canvas.<tier>.symbols.app`) are deleted — Nika
// 2026-10-23, "canvas.dev is deprecated and should be removed", and the rest
// on 2026-10-24. The editor runs inside the shell (canvasUrl.js); only the
// local canvas dev server (`canvas.localhost`) still pairs with the local shell.
//
// FA204 / frank trap — helpers stay inline in the export (see canvasUrl.js).

import { shellUrl as channelShellUrl, channelNames } from '@symbo.ls/channels'

export const isSessionBridgePeer = (origin) => {
  function _hostOf(url) {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }
  function _isLocalHost(host) {
    return !!host && (host === 'localhost' || host.endsWith('.localhost'))
  }
  // The comm hosts of a channel (commHost.js): the `my` label of its shell
  // host → `comm` (`dev.my.symbols.app` → `dev.comm.symbols.app`), and the
  // legacy `com` name the host had first (`dev.com.symbols.app`) — it answers
  // until that tier's old host redirects to the new one (docs/COMM_HOST.md).
  // A shell host without the `my` label (the demo channels) has none.
  function _commHostsFor(shellHost) {
    const m = /^((?:[a-z0-9-]+\.)*)my\.symbols\.app$/.exec(shellHost || '')
    return m ? [`${m[1]}comm.symbols.app`, `${m[1]}com.symbols.app`] : []
  }

  if (typeof origin !== 'string' || typeof window === 'undefined') return false
  let asker
  try {
    asker = new URL(origin)
  } catch {
    return false
  }
  // Reject anything that is not a bare origin ("null", paths, credentials).
  if (asker.origin !== origin) return false

  const here = window.location && window.location.hostname
  if (!here) return false
  // A local page answers ONLY the local shell and canvas hosts, on the page's
  // own port (the portless proxy, :1355). Never another *.localhost: the local
  // proxy also serves per-project preview hosts (`<owner>--<key>.preview.
  // localhost`) that render user code (review L1, 2026-10-09).
  if (_isLocalHost(here)) {
    const localPeers = ['workspace.localhost', 'canvas.localhost']
    const herePort = (window.location && window.location.port) || ''
    return localPeers.includes(asker.hostname) && (!herePort || asker.port === herePort)
  }
  if (asker.protocol !== 'https:' || asker.port) return false

  for (const name of channelNames) {
    if (name === 'local') continue
    const shell = _hostOf(channelShellUrl(name))
    if (!shell) continue
    const comms = _commHostsFor(shell)
    const onChannel = (h) => h === shell || comms.includes(h)
    if (onChannel(here) && onChannel(asker.hostname)) return true
  }
  return false
}
